import { computed, onUnmounted, type Ref } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { onAdminEvent } from './useAdminSocket';

export type Period = '1d' | '7d' | '3m' | '6m' | '1y';

function startOf(period: Period): Date {
  const now = new Date();
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  if (period === '1d') {
    return d;
  }
  if (period === '7d') {
    d.setDate(d.getDate() - 6);
    return d;
  }
  if (period === '3m') {
    d.setMonth(d.getMonth() - 3);
    return d;
  }
  if (period === '6m') {
    d.setMonth(d.getMonth() - 6);
    return d;
  }
  d.setFullYear(d.getFullYear() - 1);
  return d;
}

export function fmt(n: number, currency = 'EUR') {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(n);
}
export function fmtDec(n: number, currency = 'EUR') {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(n);
}

// ── Types exacts des APIs ─────────────────────────────────────────────────────
export interface Windowed {
  orderCount: number;
  ordersByStatus: {
    pending?: number;
    rejected?: number;
    ready?: number;
    terminated?: number;
    in_progress?: number;
  };
  revenue: number;
  paidOrderCount: number;
  averageBasket: number;
  payments: { success: number; pending: number; failed: number };
}

export interface OrgOnboarding {
  steps: {
    logo: boolean;
    description: boolean;
    taxId: boolean;
    contact: boolean;
    address: boolean;
    store: boolean;
    activeStore: boolean;
    paymentConfigured: boolean;
  };
  completed: number;
  total: number;
  percentage: number;
}

export interface OrgSnapshot {
  organizationCount?: number;
  storeCount: number;
  activeStoreCount: number;
  paymentConfiguredCount?: number;
  paymentConfiguredStoreCount?: number;
  productCount: number;
  activeProductCount?: number;
  categoryCount?: number;
  productsWithoutTranslation: number;
  mediaCount?: number;
  onboarding?: OrgOnboarding;
}

export interface StoreSnapshot {
  isActive: boolean;
  paymentConfigured: boolean;
  scheduleDefined: boolean;
  seatCount: number;
  productCount: number;
  activeProductCount: number;
  categoryCount: number;
  productsWithoutTranslation: number;
}

export interface OrgStat {
  organizationId: string;
  name: string;
  snapshot: OrgSnapshot;
  windowed: Windowed;
}

export interface StoreStat {
  storeId: string;
  designation: string;
  snapshot: StoreSnapshot;
  windowed: Windowed;
}

interface OrgStatsResponse {
  data: {
    range: { from: string; to: string };
    totals: { snapshot: OrgSnapshot; windowed: Windowed };
    organizations: OrgStat[];
  };
}

interface StoreStatsResponse {
  data: {
    range: { from: string; to: string };
    totals: { snapshot: OrgSnapshot; windowed: Windowed };
    stores: StoreStat[];
  };
}

function emptyWindowed(): Windowed {
  return {
    orderCount: 0,
    ordersByStatus: {},
    revenue: 0,
    paidOrderCount: 0,
    averageBasket: 0,
    payments: { success: 0, pending: 0, failed: 0 },
  };
}
function emptyOrgSnapshot(): OrgSnapshot {
  return {
    storeCount: 0,
    activeStoreCount: 0,
    productCount: 0,
    productsWithoutTranslation: 0,
  };
}

export function useAnalyticsData(
  period: Ref<Period>,
  orgFilter: Ref<string | null>,
  storeFilter: Ref<string | null>,
  customFrom?: Ref<string>,
  customTo?: Ref<string>,
) {
  const resolvedFrom = computed(() =>
    customFrom?.value
      ? new Date(customFrom.value).toISOString()
      : startOf(period.value).toISOString(),
  );
  const resolvedTo = computed(() =>
    customTo?.value
      ? new Date(customTo.value + 'T23:59:59').toISOString()
      : new Date().toISOString(),
  );
  // ── /organizations/stats ──────────────────────────────────────────────────
  const {
    data: orgData,
    isPending: orgsPending,
    isError: orgsError,
  } = useQuery({
    queryKey: computed(() => [
      'analytics-org-stats',
      period.value,
      customFrom?.value,
      customTo?.value,
    ]),
    queryFn: async () => {
      const from = resolvedFrom.value;
      const to = resolvedTo.value;
      const res = await axiosInstance.get<OrgStatsResponse>(
        `organizations/stats?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
        { headers: { 'x-client-app': 'admin-back-office' } },
      );
      return res.data.data;
    },
    staleTime: 5 * 60_000,
  });

  // ── /restaurants/stats ────────────────────────────────────────────────────
  const {
    data: storeData,
    isPending: storesPending,
    isError: storesError,
  } = useQuery({
    queryKey: computed(() => [
      'analytics-store-stats',
      period.value,
      orgFilter.value,
      customFrom?.value,
      customTo?.value,
    ]),
    queryFn: async () => {
      const from = resolvedFrom.value;
      const to = resolvedTo.value;
      let url = `restaurants/stats?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      if (orgFilter.value)
        url += `&organizationIds=${encodeURIComponent(orgFilter.value)}`;
      const res = await axiosInstance.get<StoreStatsResponse>(url, {
        headers: { 'x-client-app': 'admin-back-office' },
      });
      return res.data.data;
    },
    staleTime: 5 * 60_000,
  });

  // ── Données brutes exposées ───────────────────────────────────────────────
  const orgTotals = computed(
    () =>
      orgData.value?.totals ?? {
        snapshot: emptyOrgSnapshot(),
        windowed: emptyWindowed(),
      },
  );
  const storeTotals = computed(
    () =>
      storeData.value?.totals ?? {
        snapshot: emptyOrgSnapshot(),
        windowed: emptyWindowed(),
      },
  );
  const organizations = computed(() => orgData.value?.organizations ?? []);
  const stores = computed(() => storeData.value?.stores ?? []);

  // Pour le filtre org sélectionné : totaux depuis storeData
  const activeTotals = computed(
    (): { snapshot: OrgSnapshot; windowed: Windowed } => {
      if (storeFilter.value) {
        const s = stores.value.find((s) => s.storeId === storeFilter.value);
        if (s) return { snapshot: emptyOrgSnapshot(), windowed: s.windowed };
      }
      if (orgFilter.value) return storeTotals.value;
      return orgTotals.value;
    },
  );

  const activeOrgSnapshot = computed((): OrgSnapshot => {
    if (orgFilter.value) return storeTotals.value.snapshot;
    return orgTotals.value.snapshot;
  });

  // Listes pour le dropdown
  const allOrgs = computed(() =>
    organizations.value.map((o) => ({ id: o.organizationId, name: o.name })),
  );
  const orgStores = computed(() =>
    stores.value.map((s) => ({ id: s.storeId, name: s.designation })),
  );
  const allStores = orgStores;

  const totalOrders = computed(() => activeTotals.value.windowed.orderCount);

  // KPIs globaux (totaux)
  const kpis = computed(() => {
    const w = activeTotals.value.windowed;
    const s = activeOrgSnapshot.value;
    const paidPct = w.orderCount
      ? Math.round((w.paidOrderCount / w.orderCount) * 100)
      : 0;
    const pmtConf =
      s.paymentConfiguredCount ?? s.paymentConfiguredStoreCount ?? 0;
    const activeProducts = s.activeProductCount ?? s.productCount;
    const completionRate = w.orderCount
      ? Math.round(((w.ordersByStatus.terminated ?? 0) / w.orderCount) * 100)
      : 0;
    return [
      {
        icon: 'pi-euro',
        label: "Chiffre d'affaires",
        value: fmt(w.revenue),
        desc: `Panier moyen ${fmtDec(w.averageBasket)}`,
        accent: true,
      },
      {
        icon: 'pi-shopping-cart',
        label: 'Commandes',
        value: String(w.orderCount),
        desc: `Terminées ${w.ordersByStatus.terminated ?? 0} (${completionRate}%) · En cours ${w.ordersByStatus.in_progress ?? 0}`,
      },
      {
        icon: 'pi-clock',
        label: 'En attente',
        value: String(w.ordersByStatus.pending ?? 0),
        desc: `Prêtes ${w.ordersByStatus.ready ?? 0} · Rejetées ${w.ordersByStatus.rejected ?? 0}`,
      },
      {
        icon: 'pi-check-circle',
        label: 'Commandes payées',
        value: `${w.paidOrderCount} (${paidPct}%)`,
        desc: `Paiements réussis ${w.payments.success} · En attente ${w.payments.pending} · Échoués ${w.payments.failed}`,
      },
      {
        icon: 'pi-building',
        label: 'Stores actifs',
        value: `${s.activeStoreCount} / ${s.storeCount}`,
        desc: `Paiement configuré ${pmtConf} store${pmtConf > 1 ? 's' : ''}`,
      },
      {
        icon: 'pi-box',
        label: 'Produits actifs',
        value: `${activeProducts} / ${s.productCount}`,
        desc: `Sans traduction ${s.productsWithoutTranslation} · Catégories ${s.categoryCount ?? 0}`,
      },
    ];
  });

  // Graphique CA par store
  const chartData = computed(() => {
    if (storeFilter.value) {
      const s = activeTotals.value.windowed.ordersByStatus;
      return {
        ca: [
          s.terminated ?? 0,
          s.ready ?? 0,
          s.in_progress ?? 0,
          s.pending ?? 0,
          s.rejected ?? 0,
        ],
        labels: ['Terminées', 'Prêtes', 'En cours', 'En attente', 'Rejetées'],
      };
    }
    const list = orgFilter.value
      ? stores.value
      : organizations.value.map((o) => ({
          ...o,
          storeId: o.organizationId,
          designation: o.name,
        }));
    if (!list.length) return { ca: [] as number[], labels: [] as string[] };
    const sorted = [...list]
      .sort((a, b) => b.windowed.revenue - a.windowed.revenue)
      .slice(0, 12);
    return {
      ca: sorted.map((s) => s.windowed.revenue),
      labels: sorted.map((s) => {
        const n = 'designation' in s ? s.designation : (s as any).name;
        return n.length > 14 ? n.slice(0, 12) + '…' : n;
      }),
    };
  });

  // Donut paiements
  const donutSlices = computed(() => {
    const p = activeTotals.value.windowed.payments;
    const total = p.success + p.pending + p.failed || 1;
    return [
      {
        label: 'Payé',
        value: Math.round((p.success / total) * 100),
        color: '#10b981',
      },
      {
        label: 'En attente',
        value: Math.round((p.pending / total) * 100),
        color: '#fbaf01',
      },
      {
        label: 'Échoué',
        value: Math.round((p.failed / total) * 100),
        color: '#ef4444',
      },
    ].filter((s) => s.value > 0);
  });

  // Top stores par commandes
  const topProducts = computed(() => {
    const list = orgFilter.value
      ? stores.value
      : organizations.value.map((o) => ({
          storeId: o.organizationId,
          designation: o.name,
          windowed: o.windowed,
          snapshot: o.snapshot,
        }));
    if (!list.length) return [];
    const sorted = [...list]
      .sort((a, b) => b.windowed.orderCount - a.windowed.orderCount)
      .slice(0, 10);
    const maxQty = sorted[0]?.windowed.orderCount || 1;
    return sorted.map((s, i) => ({
      rank: i + 1,
      name: s.designation,
      category: '',
      qty: s.windowed.orderCount,
      revenue: fmtDec(s.windowed.revenue),
      pct: Math.round((s.windowed.orderCount / maxQty) * 100),
    }));
  });

  const isPending = computed(() => orgsPending.value || storesPending.value);
  const isError = computed(() => !!orgsError.value || !!storesError.value);

  // ── Real-time invalidation via admin socket events ─────────────────────────
  // Débouncée : une rafale d'événements proches (import en masse, etc.) ne
  // déclenche qu'un seul refetch au lieu d'un par événement.
  const queryClient = useQueryClient();

  let invalidateTimer: ReturnType<typeof setTimeout> | null = null;
  function scheduleInvalidate() {
    if (invalidateTimer) clearTimeout(invalidateTimer);
    invalidateTimer = setTimeout(() => {
      invalidateTimer = null;
      queryClient.invalidateQueries({ queryKey: ['analytics-org-stats'] });
      queryClient.invalidateQueries({ queryKey: ['analytics-store-stats'] });
    }, 3_000);
  }

  const unsub = onAdminEvent(
    [
      'store:created',
      'store:updated',
      'store:deleted',
      'organization:created',
      'organization:updated',
      'organization:deleted',
      'product:created',
      'product:updated',
      'product:deleted',
    ],
    scheduleInvalidate,
  );

  onUnmounted(() => {
    if (invalidateTimer) clearTimeout(invalidateTimer);
    unsub();
  });

  return {
    // totaux bruts
    orgTotals,
    storeTotals,
    organizations,
    stores,
    // pour la page
    kpis,
    chartData,
    donutSlices,
    topProducts,
    totalOrders,
    allOrgs,
    allStores,
    orgStores,
    isPending,
    isError,
    // Query dédiée (stores/organisation sélectionnée) — permet un skeleton
    // ciblé sur la carte "Stores de cette org" plutôt que de la faire
    // disparaître pendant le refetch déclenché par un changement d'org.
    storesPending,
  };
}
